import React, { useEffect, useMemo } from 'react';
import { array, string } from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import cx from 'classnames';
import useStyles from 'hooks/useStyles';
import qs from 'query-string';

import history from 'history.js';

import SnapaskHeader from 'components/SnapaskHeader';
import CountdownDate from 'components/CountdownDate';
import { BackIcon } from 'components/Icon';

import { COURSE, REGULAR_CLASS, GROUP_PASS } from 'constants/contentTypes';
import {
  BUNDLE_PURCHASE_TEMPLATE,
  COURSE_INFORMATION_TEMPLATE,
  LIVE_TOPIC,
} from 'constants/routingPath';
import { PURCHASE_PAYMENT_OPTION_BUNDLE_PAGE_ENTER } from 'constants/gtmEvent';
import { BUNDLE } from 'constants/checkoutCollectionTypes';

import { getCheckoutCollectionContentListById } from 'reduxModules/checkoutCollection/selector';

import useGetOfficialSiteURL from 'hooks/useGetOfficialSiteURL';
import { getBundlePageBundleDescription } from 'utils/bundle';

import { push, goBack } from 'reduxModules/navigation/action';
import { sendGTMEvent } from 'reduxModules/googleTagManager/action';

import ContentItem from 'components/ContentItem';
import BundleCart from './components/BundleCart';
import useBundleCart from './useBundleCart';

// eslint-disable-next-line css-modules/no-unused-class
import checkoutCollectionStyles from 'styles/checkoutCollection.scss';
import styles from './Bundle.scss';

export default function Bundle({ id, optionList }) {
  useStyles(styles, checkoutCollectionStyles);

  const { t } = useTranslation(['purchase']);
  const { location } = history;

  const dispatch = useDispatch();

  const getOfficialSiteURL = useGetOfficialSiteURL();

  const contentList = useSelector(getCheckoutCollectionContentListById(id));
  /**
   * course=1 -> { course: 1 }
   * course=1,2&live_topic=5 -> { course: [1,2], live_topic:5 }
   */
  const hashObject = useMemo(
    () =>
      qs.parse(location?.hash, {
        arrayFormat: 'comma',
        parseNumbers: true,
      }),
    [location?.hash],
  );

  const {
    planType,
    matchedOption,
    selectedItems,
    billingCyclePerMonths,
    selectedItemsQueryString,
    checkIsSelectedItem,
    isAddable,
    addItem,
    removeItem,
    minimumItemCount,
  } = useBundleCart({
    bundleId: id,
    optionList,
    contentList,
    hashObject,
  });

  const { liveTopics, courses, groupPasses } = contentList;

  const [firstOption] = optionList;

  const {
    title,
    hasPresale,
    presaleExpiredAt,
    plan: { highlight, currency, displayCurrency },
  } = firstOption;

  const optionCountList = getBundlePageBundleDescription(optionList, t);

  const showPromotionSection = highlight || hasPresale;
  const [contentType, contentId] = Object.entries(hashObject)
    .filter(([key]) => key === COURSE || key === REGULAR_CLASS)
    .flat();

  // use the first content id as entry
  const entryContentId = Array.isArray(contentId) ? contentId[0] : contentId;

  useEffect(() => {
    dispatch(
      sendGTMEvent({
        eventName: PURCHASE_PAYMENT_OPTION_BUNDLE_PAGE_ENTER,
        data: {
          type: contentType,
          contentId: entryContentId,
          bundleType: BUNDLE,
          checkoutCollectionId: id,
          section: location?.state?.from ? 'normal' : 'deeplink',
        },
      }),
    );
  }, [
    dispatch,
    id,
    hashObject,
    location?.state?.from,
    contentType,
    entryContentId,
  ]);

  const renderPromotionLabel = () => {
    if (highlight)
      return (
        <div className={checkoutCollectionStyles.labelAccent}>{highlight}</div>
      );
    if (hasPresale)
      return (
        <div className={checkoutCollectionStyles.label}>
          {t('purchase.promoOfferTag.limited')}
        </div>
      );

    return null;
  };

  const handleSelectContent = (type, content) => event => {
    event.preventDefault();
    event.stopPropagation();
    addItem({
      ...content,
      type,
    });
  };

  const handleRemoveContent = (type, removeId) => event => {
    event.preventDefault();
    event.stopPropagation();
    removeItem({
      type,
      contentId: removeId,
    });
  };

  const handleViewContentPage = contentPageUrl => () => {
    dispatch(
      push({
        pathname: contentPageUrl,
        hash: '#view',
        state: {
          from: location.pathname,
        },
      }),
    );
  };

  const handleGoPurchase = checkoutCollectionId => () => {
    const fromContent =
      contentType && entryContentId
        ? `&from=${contentType}:${entryContentId}`
        : '';

    return dispatch(
      push(
        getOfficialSiteURL(
          `${BUNDLE_PURCHASE_TEMPLATE}#${selectedItemsQueryString}${fromContent}`,
          {
            id: checkoutCollectionId,
          },
        ),
      ),
    );
  };
  return (
    <>
      <SnapaskHeader />
      <main className={styles.container}>
        {history.length > 1 && (
          <BackIcon
            width={24}
            height={24}
            className={styles.backIcon}
            onClick={() => dispatch(goBack())}
          />
        )}
        <BundleCart
          title={title}
          billingCyclePerMonths={billingCyclePerMonths}
          planType={planType}
          items={selectedItems}
          contentType={contentType}
          currency={currency}
          displayCurrency={displayCurrency}
          matchedOption={matchedOption}
          minimumItemCount={minimumItemCount}
          onRemove={handleRemoveContent}
          onCheckout={handleGoPurchase}
        />
        {showPromotionSection && (
          <div
            className={cx(checkoutCollectionStyles.promotion, styles.promotion)}
          >
            {renderPromotionLabel()}
            {hasPresale && <CountdownDate dateTo={presaleExpiredAt} />}
          </div>
        )}
        <h3 className={styles.title}>{title}</h3>
        {optionCountList.length > 0 && (
          <div className={styles.options}>
            {optionCountList.map(({ option, key }) => (
              <div key={key}>
                <div
                  className={styles.tag}
                  dangerouslySetInnerHTML={{
                    __html: option,
                  }}
                />
              </div>
            ))}
          </div>
        )}
        <ul className={styles.contentList}>
          {liveTopics.map(liveTopic => (
            <li key={`${REGULAR_CLASS}-${liveTopic.id}`}>
              <ContentItem
                onClick={handleViewContentPage(
                  getOfficialSiteURL(LIVE_TOPIC, {
                    id: liveTopic.id,
                  }),
                )}
                contentType={contentType}
                isSelectable={isAddable}
                isSelected={checkIsSelectedItem(REGULAR_CLASS, liveTopic.id)}
                onRemove={handleRemoveContent(REGULAR_CLASS, liveTopic.id)}
                onSelect={handleSelectContent(REGULAR_CLASS, liveTopic)}
                imageUrl={liveTopic.thumbnailUrl}
                name={liveTopic.name}
                frequency={liveTopic.frequency}
              />
            </li>
          ))}

          {courses.map(course => (
            <li key={`${COURSE}-${course.id}`}>
              <ContentItem
                onClick={handleViewContentPage(
                  getOfficialSiteURL(COURSE_INFORMATION_TEMPLATE, {
                    id: course.id,
                  }),
                )}
                contentType={contentType}
                isSelectable={isAddable}
                isSelected={checkIsSelectedItem(COURSE, course.id)}
                onRemove={handleRemoveContent(COURSE, course.id)}
                onSelect={handleSelectContent(COURSE, course)}
                imageUrl={course.thumbnailUrl}
                totalLessonsDurationTime={course.totalLessonsDurationTime}
                lessonCount={course.lessonCount}
                name={course.name}
              />
            </li>
          ))}
          {groupPasses.map(groupPass => (
            <li key={`${GROUP_PASS}-${groupPass.id}`}>
              <ContentItem
                isSelectable={isAddable}
                contentType={contentType}
                isSelected={checkIsSelectedItem(GROUP_PASS, groupPass.id)}
                onRemove={handleRemoveContent(GROUP_PASS, groupPass.id)}
                onSelect={handleSelectContent(GROUP_PASS, groupPass)}
                imageUrl={groupPass.thumbnailUrl}
                name={groupPass.name}
                description={t('purchase.content.cta.bundleTotal', {
                  course: groupPass.courseAmount,
                })}
              />
            </li>
          ))}
        </ul>
      </main>
    </>
  );
}

Bundle.propTypes = {
  id: string.isRequired,
  optionList: array.isRequired,
};
